import type { ObservableMap } from 'mobx'
import type { InteractiveFormValidationSchema } from './schema'
import type { FormFieldError } from '../../shared/FormCubit'
import { z } from 'zod'

export const ValidationError = z.enum([
  'is_required',
  'must_be_a_string',
  'must_be_a_string_array',
  'must_be_longer_than',
  'must_be_shorter_than',
  'must_have_at_least',
  'must_have_at_most',
])

export type ValidationErrors = z.infer<typeof ValidationError>

export type JSONSchemaOutputValidatorError = FormFieldError<ValidationErrors>

export class JSONSchemaOutputValidator {
  schema: InteractiveFormValidationSchema
  values: ObservableMap<string, unknown>
  fieldName?: string

  constructor(
    schema: InteractiveFormValidationSchema,
    values: ObservableMap<string, unknown>,
    fieldName?: string
  ) {
    this.schema = schema
    this.values = values
    this.fieldName = fieldName
  }

  validate(): {
    valid: boolean
    errors: Record<string, JSONSchemaOutputValidatorError[]>
  } {
    const errors: Record<string, JSONSchemaOutputValidatorError[]> = {}
    const schema = this.schema

    const keys = this.fieldName
      ? [this.fieldName]
      : Object.keys(schema.properties)
    const required = schema.required || []
    for (const key of keys) {
      const val = this.values.get(key)
      const prop = schema.properties[key]
      const isRequired = required.includes(key)
      const valIsEmpty =
        val === undefined ||
        val === null ||
        val === '' ||
        (Array.isArray(val) && val.length === 0)
      if (prop && isRequired && valIsEmpty) {
        errors[key] ||= []
        errors[key].push({ type: 'required', i18nKey: 'is_required' })
      }
    }
    // now, we need to run individual property validations
    for (const key of keys) {
      const val = this.values.get(key)
      const prop = schema.properties[key]
      if (!prop || val === undefined || val === null) continue

      if (prop.type === 'string') {
        if (typeof val !== 'string') {
          errors[key] ||= []
          errors[key].push({ type: 'invalid', i18nKey: 'must_be_a_string' })
          continue
        }

        if (
          prop.minLength !== undefined &&
          val &&
          val.length < prop.minLength
        ) {
          errors[key] ||= []
          errors[key].push({
            type: 'min',
            i18nKey: 'must_be_longer_than',
            args: { minLength: prop.minLength },
          })
          continue
        }

        if (
          prop.maxLength !== undefined &&
          val &&
          val.length > prop.maxLength
        ) {
          errors[key] ||= []
          errors[key].push({
            type: 'max',
            i18nKey: 'must_be_shorter_than',
            args: { maxLength: prop.maxLength },
          })
          continue
        }
      } else if (prop.type === 'array' && prop.items.type === 'string') {
        if (!Array.isArray(val) || !val.every((v) => typeof v === 'string')) {
          errors[key] ||= []
          errors[key].push({
            type: 'invalid',
            i18nKey: 'must_be_a_string_array',
          })
          continue
        }
        const arr = val as string[]
        if (
          prop.minItems !== undefined &&
          // when 0, is_required should catch it
          arr.length > 0 &&
          arr.length < prop.minItems
        ) {
          errors[key] ||= []
          errors[key].push({
            type: 'min',
            i18nKey: 'must_have_at_least',
            args: { min: prop.minItems },
          })
          continue
        }
        if (prop.maxItems !== undefined && arr.length > prop.maxItems) {
          errors[key] ||= []
          errors[key].push({
            type: 'max',
            i18nKey: 'must_have_at_most',
            args: { max: prop.maxItems },
          })
          continue
        }
      }
    }

    const valid = Object.keys(errors).length === 0

    return { valid, errors }
  }
}
